// ============================================================================
// Events
// ============================================================================
import {
  sidebar, sidebarOverlay, toggleSidebarBtn, sbNew,
  dirDisplay, dirBadge, onboardStart, sendBtn, stopBtn,
  input, composerInput, attachBtn, fileInput, pastePreview,
  modeBadge, mpCurrent, fvOverlay, reconnectBtn,
} from "./dom.js";
import {
  showOnboarding, setStatus, setBusy, setTyping,
  scrollDown, renderQueuedTray, initViewManager,
} from "./view-manager.js";
import { renderSidebar, initSidebar } from "./sidebar.js";
import { initMessageRendering, sysMsg } from "./message-rendering.js";
import {
  initWebSocket, send, connect, hideReconnectBanner, resetReconnectState,
  probeOrReconnect,
} from "./websocket-transport.js";
import { clearActiveSession } from "./persistence.js";
import { restoreCachedNodes } from "./cache.js";
import { closeModelDropdown, initModelPicker } from "./model-picker.js";
import { openFileViewer } from "./file-viewer.js";
import {
  initPins, closePinPeek, isPinPeekOpen,
  toggleFilePin as toggleFilePinOp, toggleFilePinGlobal as toggleFilePinGlobalOp,
  filePinTier as filePinTierOp,
} from "./pins.js";
import { closeModeMenu, isModeMenuOpen, renderModeMenu, initModePicker } from "./mode-picker.js";
import {
  initSlashCommandPicker, openCommandPicker, closeCommandPicker,
  executeSelectedCommand, navigateCommandPicker, isCommandPickerOpen,
} from "./slash-command-picker.js";
import {
  initFileMentionPicker, updateFileMention, closeFileMention,
  executeSelectedFileMention, navigateFileMention, isFileMentionOpen,
} from "./file-mention-picker.js";
import { closeFileViewer, initFileViewer, openFileInViewer, fvToast, initImageZoom } from "./file-viewer.js";
import { initElicitation } from "./elicitation.js";
import { initWidgets } from "./widgets.js";
import { initCopyButtons } from "./copy.js";
import {
  startSession, sendPrompt, newSession, changeFolder, doStartSession,
  modeShortLabel, MODE_COMMAND_ALIASES,
} from "./session-control.js";

// Deps populated by initEvents()
let getSelectedDir = () => "";
let setSelectedDir = null;
let getWs = () => null;
let setWs = null;
let getSessionActive = () => false;
let setSessionActive = null;
let getPendingStart = () => null;
let setPendingStart = null;
let getResuming = () => false;
let setResuming = null;
let getIntentionalClose = () => false;
let setIntentionalClose = null;
let getCurrentSessionId = () => null;
let getActiveCwd = () => "";
let getExpectedSessionId = () => null;
let setExpectedSessionId = null;
let getPendingDeepLinkSession = () => null;
let setPendingDeepLinkSession = null;
let getSessions = () => [];
let setSessions = null;
let getLiveSessionIds = () => new Set();
let setLiveSessionIds = null;
let getDeletedSessionIds = () => new Set();
let getConfigOptions = () => [];
let setConfigOptions = null;
let getAvailableModes = () => [];
let setAvailableModes = null;
let getCurrentModeId = () => null;
let setCurrentModeId = null;
let getTurboEnabled = () => false;
let setTurboEnabled = null;
let getLoadingHistory = () => false;
let setLoadingHistory = null;
let getReplayingHistory = () => false;
let setReplayingHistory = null;
let getReplayTotal = () => 0;
let setReplayTotal = null;
let getReplayReceived = () => 0;
let setReplayReceived = null;
let getReplayPageSize = () => 50;
let setReplayPageSize = null;
let getReplayOldestCursor = () => null;
let setReplayOldestCursor = null;
let getReplayFirstPageRendered = () => false;
let setReplayFirstPageRendered = null;
let getReplayLoaderEl = () => null;
let setReplayLoaderEl = null;
let getScrollBackFetching = () => false;
let setScrollBackFetching = null;
let getQueuedPromptCount = () => 0;
let setQueuedPromptCount = null;
let getQueueState = () => ({ running: null, queued: [] });
let setQueueState = null;
let getCacheTimer = () => null;
let setCacheTimer = null;
let getAgentMsgs = () => new Map();
let getToolCards = () => new Map();
let getMaxDomMessages = () => 500;
let getUserPinnedAway = () => false;
let setUserPinnedAway = null;
let getScrollTimer = () => null;
let setScrollTimer = null;
let getCacheRestoreToken = () => 0;
let incrementCacheRestoreToken = null;
let getPendingImages = () => [];
let setPendingImages = null;
let getLiveCwds = () => new Set();
let getAvailableCommands = () => [];
let setAvailableCommands = null;
let getBusy = () => false;
let setBusyState = null;

let setActiveCwdRef = null;
let setSessionIdRef = null;
let renderRecentDirsRef = null;
let removeRecentDirRef = null;
let fetchLiveCwdsRef = null;
let recomputeLiveCwdsRef = null;
let loadSessionFromHistoryRef = null;
let resetNodeCacheStateRef = null;
let trimDomMessagesRef = null;
let debouncedCacheMessagesRef = null;
let cachePageNodesRef = null;
let cacheVisibleNodesRef = null;

export function openSidebar() {
  sidebar.classList.remove("collapsed");
  sidebarOverlay.classList.remove("hidden");
}
export function closeSidebar() {
  sidebar.classList.add("collapsed");
  sidebarOverlay.classList.add("hidden");
}

// Update the mode badge in the header
function updateModeIndicator() {
  const turbo = getTurboEnabled();
  if ((!getCurrentModeId() && !turbo) || !getSessionActive()) {
    modeBadge.classList.add("hidden");
    closeModeMenu();
    return;
  }
  modeBadge.classList.remove("hidden");
  // Turbo is a gateway-level switch layered over the ACP mode — when on,
  // it takes over the badge label regardless of the underlying mode.
  // Otherwise use a short label (≤6 chars); the full name + description
  // from the CLI is shown in the picker modal (renderModeMenu).
  modeBadge.textContent = turbo ? "turbo" : modeShortLabel(getCurrentModeId());
  // Reset to base classes, then re-add the mode-specific and status classes.
  // (Preserve status-* classes set by setStatus so the border color sticks.)
  const statusClasses = [...modeBadge.classList].filter(c => c.startsWith("status-"));
  modeBadge.className = "mode-badge " + (turbo ? "mode-turbo" : "mode-" + getCurrentModeId());
  for (const sc of statusClasses) modeBadge.classList.add(sc);
  // If the menu is open, refresh its selection highlight.
  if (isModeMenuOpen()) renderModeMenu();
}

// ---- Image paste / drag-drop / attach ----

// Models known to NOT support vision (text-only). All others assumed vision-capable.
const NON_VISION_PATTERNS = ["swe-1", "inkling", "nemotron", "deepseek"];
function isVisionCapable() {
  const model = (mpCurrent.textContent || "").toLowerCase();
  const modelUid = getCurrentModelUid();
  const check = (modelUid || model);
  if (!check) return true; // unknown — assume yes
  return !NON_VISION_PATTERNS.some((p) => check.includes(p));
}
function getCurrentModelUid() {
  const modelConfig = getConfigOptions().find(
    (o) => o.category === "model" || o.category === "model_config" || o.id.includes("model")
  );
  if (!modelConfig) return "";
  const cv = modelConfig.currentValue;
  const opt = (modelConfig.options || []).find((o) => o.value === cv);
  return opt?.value || cv || "";
}

export function renderPastePreview() {
  const pendingImages = getPendingImages();
  pastePreview.innerHTML = "";
  for (let i = 0; i < pendingImages.length; i++) {
    const img = pendingImages[i];
    const thumb = document.createElement("div");
    thumb.className = "paste-thumb";
    const imgEl = document.createElement("img");
    imgEl.src = `data:${img.mimeType};base64,${img.data}`;
    thumb.appendChild(imgEl);
    const removeBtn = document.createElement("button");
    removeBtn.className = "paste-remove";
    removeBtn.textContent = "×";
    removeBtn.onclick = () => { haptic("tap"); pendingImages.splice(i, 1); renderPastePreview(); };
    thumb.appendChild(removeBtn);
    pastePreview.appendChild(thumb);
  }
  // Vision warning
  let warn = pastePreview.querySelector(".vision-warn");
  if (pendingImages.length && !isVisionCapable()) {
    if (!warn) {
      warn = document.createElement("div");
      warn.className = "vision-warn";
      warn.innerHTML = "⚠ Current model may not support images. Consider switching to Claude, GPT, Gemini, or Grok.";
      pastePreview.appendChild(warn);
    }
  } else if (warn) {
    warn.remove();
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // result is "data:image/png;base64,...." — extract the base64 part
      const comma = result.indexOf(",");
      const meta = result.substring(5, comma); // "image/png;base64"
      const mimeType = meta.split(";")[0];
      const data = result.substring(comma + 1);
      resolve({ data, mimeType });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Compress/resize image via canvas to keep WebSocket messages small.
// Mobile photos can be 10MB+; we resize to max 1568px and re-encode as JPEG.
function compressImage(file, maxDim = 1568, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      // PNG with transparency stays PNG; everything else becomes JPEG
      const mime = (file.type === "image/png") ? "image/png" : "image/jpeg";
      const dataUrl = canvas.toDataURL(mime, quality);
      const comma = dataUrl.indexOf(",");
      resolve({ data: dataUrl.substring(comma + 1), mimeType: mime });
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to load image")); };
    img.src = url;
  });
}

async function handleImageFiles(files) {
  const imageFiles = files.filter((f) => f.type.startsWith("image/"));
  const pendingImages = getPendingImages();
  for (const f of imageFiles) {
    try {
      const img = await compressImage(f);
      pendingImages.push(img);
    } catch (e) {
      // Fallback: try raw base64 without compression
      try {
        const img = await fileToBase64(f);
        pendingImages.push(img);
      } catch { /* ignore */ }
    }
  }
  renderPastePreview();
}

/**
 * Initialize all DOM event listeners and sub-module init calls.
 * @param {object} deps
 */
export function initEvents(deps) {
  // State accessors
  getSelectedDir = deps.getSelectedDir;
  setSelectedDir = deps.setSelectedDir;
  getWs = deps.getWs;
  setWs = deps.setWs;
  getSessionActive = deps.getSessionActive;
  setSessionActive = deps.setSessionActive;
  getPendingStart = deps.getPendingStart;
  setPendingStart = deps.setPendingStart;
  getResuming = deps.getResuming;
  setResuming = deps.setResuming;
  getIntentionalClose = deps.getIntentionalClose;
  setIntentionalClose = deps.setIntentionalClose;
  getCurrentSessionId = deps.getCurrentSessionId;
  getActiveCwd = deps.getActiveCwd;
  getExpectedSessionId = deps.getExpectedSessionId;
  setExpectedSessionId = deps.setExpectedSessionId;
  getPendingDeepLinkSession = deps.getPendingDeepLinkSession;
  setPendingDeepLinkSession = deps.setPendingDeepLinkSession;
  getSessions = deps.getSessions;
  setSessions = deps.setSessions;
  getLiveSessionIds = deps.getLiveSessionIds;
  setLiveSessionIds = deps.setLiveSessionIds;
  getDeletedSessionIds = deps.getDeletedSessionIds;
  getConfigOptions = deps.getConfigOptions;
  setConfigOptions = deps.setConfigOptions;
  getAvailableModes = deps.getAvailableModes;
  setAvailableModes = deps.setAvailableModes;
  getCurrentModeId = deps.getCurrentModeId;
  setCurrentModeId = deps.setCurrentModeId;
  getTurboEnabled = deps.getTurboEnabled || (() => false);
  setTurboEnabled = deps.setTurboEnabled || (() => {});
  getLoadingHistory = deps.getLoadingHistory;
  setLoadingHistory = deps.setLoadingHistory;
  getReplayingHistory = deps.getReplayingHistory;
  setReplayingHistory = deps.setReplayingHistory;
  getReplayTotal = deps.getReplayTotal;
  setReplayTotal = deps.setReplayTotal;
  getReplayReceived = deps.getReplayReceived;
  setReplayReceived = deps.setReplayReceived;
  getReplayPageSize = deps.getReplayPageSize;
  setReplayPageSize = deps.setReplayPageSize;
  getReplayOldestCursor = deps.getReplayOldestCursor;
  setReplayOldestCursor = deps.setReplayOldestCursor;
  getReplayFirstPageRendered = deps.getReplayFirstPageRendered;
  setReplayFirstPageRendered = deps.setReplayFirstPageRendered;
  getReplayLoaderEl = deps.getReplayLoaderEl;
  setReplayLoaderEl = deps.setReplayLoaderEl;
  getScrollBackFetching = deps.getScrollBackFetching;
  setScrollBackFetching = deps.setScrollBackFetching;
  getQueuedPromptCount = deps.getQueuedPromptCount;
  setQueuedPromptCount = deps.setQueuedPromptCount;
  getQueueState = deps.getQueueState;
  setQueueState = deps.setQueueState;
  getCacheTimer = deps.getCacheTimer;
  setCacheTimer = deps.setCacheTimer;
  getAgentMsgs = deps.getAgentMsgs;
  getToolCards = deps.getToolCards;
  getMaxDomMessages = deps.getMaxDomMessages;
  getUserPinnedAway = deps.getUserPinnedAway;
  setUserPinnedAway = deps.setUserPinnedAway;
  getScrollTimer = deps.getScrollTimer;
  setScrollTimer = deps.setScrollTimer;
  getCacheRestoreToken = deps.getCacheRestoreToken;
  incrementCacheRestoreToken = deps.incrementCacheRestoreToken;
  getPendingImages = deps.getPendingImages;
  setPendingImages = deps.setPendingImages;
  getLiveCwds = deps.getLiveCwds;
  getAvailableCommands = deps.getAvailableCommands;
  setAvailableCommands = deps.setAvailableCommands;
  getBusy = deps.getBusy;
  setBusyState = deps.setBusyState;

  // Functions from main.js
  setActiveCwdRef = deps.setActiveCwd;
  setSessionIdRef = deps.setSessionId;
  renderRecentDirsRef = deps.renderRecentDirs;
  removeRecentDirRef = deps.removeRecentDir;
  fetchLiveCwdsRef = deps.fetchLiveCwds;
  recomputeLiveCwdsRef = deps.recomputeLiveCwds;
  loadSessionFromHistoryRef = deps.loadSessionFromHistory;
  resetNodeCacheStateRef = deps.resetNodeCacheState;
  trimDomMessagesRef = deps.trimDomMessages;
  debouncedCacheMessagesRef = deps.debouncedCacheMessages;
  cachePageNodesRef = deps.cachePageNodes;
  cacheVisibleNodesRef = deps.cacheVisibleNodes;

  // ---- DOM event listeners ----

  dirDisplay.addEventListener("click", () => { haptic("tap"); openFileViewer({ intent: "pick", mode: "onboard" }); });
  dirBadge.addEventListener("click", () => { haptic("tap"); openFileViewer({ intent: "pick", mode: "change" }); });
  onboardStart.addEventListener("click", () => { haptic("tap"); startSession(); });
  sendBtn.addEventListener("click", () => { haptic("tap"); sendPrompt(); });
  stopBtn.addEventListener("click", () => { haptic("press"); send({ type: "cancel" }); });

  toggleSidebarBtn.addEventListener("click", () => { haptic("tap");
    if (sidebar.classList.contains("collapsed")) openSidebar();
    else closeSidebar();
  });
  sidebarOverlay.addEventListener("click", () => { haptic("tap"); closeSidebar(); });
  sbNew.addEventListener("click", () => { haptic("tap"); newSession(); });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing && e.keyCode !== 229) {
      e.preventDefault();
      if (isCommandPickerOpen()) {
        // Command picker is open — execute selected command
        executeSelectedCommand();
      } else if (isFileMentionOpen()) {
        // File-mention picker is open — insert selected file
        executeSelectedFileMention();
      } else {
        sendPrompt();
      }
    }
    // Command picker navigation
    if (isCommandPickerOpen()) {
      if (e.key === "ArrowDown") { e.preventDefault(); navigateCommandPicker(1); }
      if (e.key === "ArrowUp") { e.preventDefault(); navigateCommandPicker(-1); }
      if (e.key === "Escape") { e.preventDefault(); closeCommandPicker(); }
      if (e.key === "Tab") { e.preventDefault(); /* don't tab away from picker */ }
    }
    // File-mention picker navigation
    if (isFileMentionOpen()) {
      if (e.key === "ArrowDown") { e.preventDefault(); navigateFileMention(1); }
      if (e.key === "ArrowUp") { e.preventDefault(); navigateFileMention(-1); }
      if (e.key === "Escape") { e.preventDefault(); closeFileMention(); }
      if (e.key === "Tab") { e.preventDefault(); executeSelectedFileMention(); }
    }
  });
  input.addEventListener("input", () => {
    input.style.height = "auto";
    const max = window.innerWidth <= 600 ? 160 : 200;
    input.style.height = Math.min(max, input.scrollHeight) + "px";
    // Show command picker when input starts with /
    const val = input.value;
    if (val.startsWith("/")) {
      closeFileMention();
      openCommandPicker(val.slice(1));
    } else {
      closeCommandPicker();
      // Show file-mention picker when an @ token is being typed
      updateFileMention();
    }
  });

  input.addEventListener("paste", (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      handleImageFiles(files);
    }
  });

  composerInput.addEventListener("dragover", (e) => {
    if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
  });
  composerInput.addEventListener("drop", (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    handleImageFiles([...e.dataTransfer.files]);
  });
  composerInput.addEventListener("click", (e) => {
    if (e.target.closest("button, textarea, input[type=file]")) return;
    input.focus();
  });

  // Attach button — open file picker
  attachBtn.addEventListener("click", () => { haptic("tap"); fileInput.click(); });
  fileInput.addEventListener("change", () => {
    if (fileInput.files?.length) handleImageFiles([...fileInput.files]);
    fileInput.value = ""; // reset so same file can be re-selected
  });

  // ---- Sub-module init calls ----

  // View manager — handled by initViewManager in view-manager.js
  initViewManager({
    closeSidebar,
    renderRecentDirs: renderRecentDirsRef,
    clearActiveSession,
    send,
    getSessionActive: () => getSessionActive(),
    setSessionActive: (v) => { setSessionActive(v); },
    setBusyState: (v) => { setBusyState(v); },
    getQueueState: () => getQueueState(),
    getUserPinnedAway: () => getUserPinnedAway(),
    setUserPinnedAway: (v) => { setUserPinnedAway(v); },
    getScrollTimer: () => getScrollTimer(),
    setScrollTimer: (v) => { setScrollTimer(v); },
    getScrollBackFetching: () => getScrollBackFetching(),
    setScrollBackFetching: (v) => { setScrollBackFetching(v); },
    getReplayingHistory: () => getReplayingHistory(),
    getReplayOldestCursor: () => getReplayOldestCursor(),
    getWs: () => getWs(),
    trimDomMessages: trimDomMessagesRef,
  });

  // Sidebar — handled by initSidebar in sidebar.js
  initSidebar({
    getSessions: () => getSessions(),
    setSessions: (v) => { setSessions(v); },
    getLiveSessionIds: () => getLiveSessionIds(),
    getCurrentSessionId: () => getCurrentSessionId(),
    getDeletedSessionIds: () => getDeletedSessionIds(),
    getAgentMsgs: () => getAgentMsgs(),
    getToolCards: () => getToolCards(),
    getLoadingHistory: () => getLoadingHistory(),
    setLoadingHistory: (v) => { setLoadingHistory(v); },
    getCacheRestoreToken: () => getCacheRestoreToken(),
    incrementCacheRestoreToken: () => { incrementCacheRestoreToken(); },
    getResuming: () => getResuming(),
    setResuming: (v) => { setResuming(v); },
    getExpectedSessionId: () => getExpectedSessionId(),
    setExpectedSessionId: (v) => { setExpectedSessionId(v); },
    getSelectedDir: () => getSelectedDir(),
    setSelectedDir: (v) => { setSelectedDir(v); },
    getUserPinnedAway: () => getUserPinnedAway(),
    setUserPinnedAway: (v) => { setUserPinnedAway(v); },
    getSessionActive: () => getSessionActive(),
    setSessionActive: (v) => { setSessionActive(v); },
    send,
    setStatus,
    setActiveCwd: setActiveCwdRef,
    setSessionId: setSessionIdRef,
    showOnboarding,
    resetNodeCacheState: resetNodeCacheStateRef,
    restoreCachedNodes,
    scrollDown,
    sysMsg,
  });

  // Message rendering — handled by initMessageRendering in message-rendering.js
  initMessageRendering({
    scrollDown,
    trimDomMessages: trimDomMessagesRef,
    debouncedCacheMessages: debouncedCacheMessagesRef,
    send,
    updateModeIndicator,
    agentMsgs: getAgentMsgs(),
    toolCards: getToolCards(),
    getReplayingHistory: () => getReplayingHistory(),
    getReplayTotal: () => getReplayTotal(),
    getReplayReceived: () => getReplayReceived(),
    getReplayLoaderEl: () => getReplayLoaderEl(),
    setReplayLoaderEl: (v) => { setReplayLoaderEl(v); },
    getLoadingHistory: () => getLoadingHistory(),
    setLoadingHistory: (v) => { setLoadingHistory(v); },
    getActiveCwd: () => getActiveCwd(),
    setAvailableCommands: (v) => { setAvailableCommands(v); },
    setCurrentModeId: (v) => { setCurrentModeId(v); },
  });

  // WebSocket transport — handled by initWebSocket in websocket-transport.js
  initWebSocket({
    // State — WebSocket connection
    getWs: () => getWs(),
    setWs: (v) => { setWs(v); },
    // State — session
    getSessionActive: () => getSessionActive(),
    setSessionActive: (v) => { setSessionActive(v); },
    getPendingStart: () => getPendingStart(),
    setPendingStart: (v) => { setPendingStart(v); },
    getResuming: () => getResuming(),
    setResuming: (v) => { setResuming(v); },
    getIntentionalClose: () => getIntentionalClose(),
    setIntentionalClose: (v) => { setIntentionalClose(v); },
    getCurrentSessionId: () => getCurrentSessionId(),
    getActiveCwd: () => getActiveCwd(),
    getExpectedSessionId: () => getExpectedSessionId(),
    setExpectedSessionId: (v) => { setExpectedSessionId(v); },
    getPendingDeepLinkSession: () => getPendingDeepLinkSession(),
    setPendingDeepLinkSession: (v) => { setPendingDeepLinkSession(v); },
    getSelectedDir: () => getSelectedDir(),
    setSelectedDir: (v) => { setSelectedDir(v); },
    // State — sessions list
    getSessions: () => getSessions(),
    setSessions: (v) => { setSessions(v); },
    getLiveSessionIds: () => getLiveSessionIds(),
    setLiveSessionIds: (v) => { setLiveSessionIds(v); },
    getDeletedSessionIds: () => getDeletedSessionIds(),
    // State — config/modes
    getConfigOptions: () => getConfigOptions(),
    setConfigOptions: (v) => { setConfigOptions(v); },
    getAvailableModes: () => getAvailableModes(),
    setAvailableModes: (v) => { setAvailableModes(v); },
    getCurrentModeId: () => getCurrentModeId(),
    setCurrentModeId: (v) => { setCurrentModeId(v); },
    getTurboEnabled: () => getTurboEnabled(),
    setTurboEnabled: (v) => { setTurboEnabled(v); },
    // State — replay/history
    getLoadingHistory: () => getLoadingHistory(),
    setLoadingHistory: (v) => { setLoadingHistory(v); },
    incrementCacheRestoreToken: () => { incrementCacheRestoreToken(); },
    getReplayingHistory: () => getReplayingHistory(),
    setReplayingHistory: (v) => { setReplayingHistory(v); },
    getReplayTotal: () => getReplayTotal(),
    setReplayTotal: (v) => { setReplayTotal(v); },
    getReplayReceived: () => getReplayReceived(),
    setReplayReceived: (v) => { setReplayReceived(v); },
    getReplayPageSize: () => getReplayPageSize(),
    setReplayPageSize: (v) => { setReplayPageSize(v); },
    getReplayOldestCursor: () => getReplayOldestCursor(),
    setReplayOldestCursor: (v) => { setReplayOldestCursor(v); },
    getReplayFirstPageRendered: () => getReplayFirstPageRendered(),
    setReplayFirstPageRendered: (v) => { setReplayFirstPageRendered(v); },
    getReplayLoaderEl: () => getReplayLoaderEl(),
    setReplayLoaderEl: (v) => { setReplayLoaderEl(v); },
    getScrollBackFetching: () => getScrollBackFetching(),
    setScrollBackFetching: (v) => { setScrollBackFetching(v); },
    // State — queue
    getQueuedPromptCount: () => getQueuedPromptCount(),
    setQueuedPromptCount: (v) => { setQueuedPromptCount(v); },
    getQueueState: () => getQueueState(),
    setQueueState: (v) => { setQueueState(v); },
    // State — cache
    getCacheTimer: () => getCacheTimer(),
    setCacheTimer: (v) => { setCacheTimer(v); },
    getAgentMsgs: () => getAgentMsgs(),
    getToolCards: () => getToolCards(),
    getMaxDomMessages: () => getMaxDomMessages(),
    // Functions
    setStatus,
    setBusy,
    showOnboarding,
    doStartSession,
    setSessionId: setSessionIdRef,
    setActiveCwd: setActiveCwdRef,
    recomputeLiveCwds: recomputeLiveCwdsRef,
    loadSessionFromHistory: loadSessionFromHistoryRef,
    updateModeIndicator,
    scrollDown,
    resetNodeCacheState: resetNodeCacheStateRef,
    cachePageNodes: cachePageNodesRef,
    cacheVisibleNodes: cacheVisibleNodesRef,
    trimDomMessages: trimDomMessagesRef,
    renderSidebar,
    renderQueuedTray,
    setTyping,
  });

  // Model picker events — handled by initModelPicker in model-picker.js
  initModelPicker(send, renderPastePreview);

  // Mode picker events — handled by initModePicker in mode-picker.js
  initModePicker({
    availableModes: () => getAvailableModes(),
    currentModeId: () => getCurrentModeId(),
    turboEnabled: () => getTurboEnabled(),
    sessionActive: () => getSessionActive(),
    send: send,
  });

  // Session key for pin attribution: real sessionId, "pending" while a
  // session is starting/resuming (no id assigned yet), or null → global tier.
  const forSessionKey = () =>
    getCurrentSessionId() || (getPendingStart() || getResuming() ? "pending" : null);

  // Pins — session working-set of docs & snippets (strip + peek popover).
  initPins({
    getSessionKey: forSessionKey,
    openFileAt: (path, name) => openFileInViewer(path, name),
    toast: (msg) => fvToast(msg),
  });
  // Slash command picker — handled by initSlashCommandPicker in slash-command-picker.js
  initSlashCommandPicker({
    availableCommands: () => getAvailableCommands(),
    availableModes: () => getAvailableModes(),
    modeCommandAliases: MODE_COMMAND_ALIASES,
    input: () => input,
    insertText: (text) => { input.value = text; },
    send: sendPrompt,
  });
  // File-mention picker — handled by initFileMentionPicker in file-mention-picker.js
  initFileMentionPicker({
    activeCwd: () => getActiveCwd(),
    input: () => input,
    insertText: (text) => { input.value = text; },
    haptic: haptic,
  });
  // File viewer — handled by initFileViewer in file-viewer.js.
  // Serves both browse (Files button, file links) and pick intents
  // (onboarding dir-display, in-chat dir-badge).
  initFileViewer({
    activeCwd: () => getActiveCwd(),
    closeSidebar: closeSidebar,
    selectedDir: () => getSelectedDir(),
    setSelectedDir: (d) => { setSelectedDir(d); },
    startSession: startSession,
    changeFolder: changeFolder,
    liveCwds: () => getLiveCwds(),
    removeRecentDir: removeRecentDirRef,
    forSessionKey,
    pinOps: {
      toggleFilePin: (path, name, sessionKey) => toggleFilePinOp(path, name, sessionKey),
      toggleFilePinGlobal: (path, name) => toggleFilePinGlobalOp(path, name),
      filePinTier: (path, sessionKey) => filePinTierOp(path, sessionKey),
    },
  });
  initImageZoom();
  // Elicitation cards, _meta.widgets, copy buttons — handled by their modules
  initElicitation({ send, scrollDown });
  initWidgets({ send });
  initCopyButtons();

  // ---- Global event listeners ----

  document.addEventListener("keydown", (e) => { if (e.key === "Escape") { if (isModeMenuOpen()) { closeModeMenu(); return; } if (isCommandPickerOpen()) { closeCommandPicker(); return; } if (isFileMentionOpen()) { closeFileMention(); return; } if (isPinPeekOpen()) { closePinPeek(); return; } if (!fvOverlay.hidden) closeFileViewer(); closeSidebar(); closeModelDropdown(); } });
  document.addEventListener("click", () => { if (isModeMenuOpen()) closeModeMenu(); });
  window.addEventListener("blur", () => { if (isModeMenuOpen()) closeModeMenu(); });

  // Proactive reconnect on refocus. Mobile browsers (Android Chrome/PWA)
  // freeze timers and kill idle sockets while backgrounded; the scheduled
  // reconnect may never have fired. Probe the WS the moment the page is
  // visible again so the user lands on a live session without interacting.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) probeOrReconnect();
  });
  // bfcache restore (back/forward navigation on mobile): the page is
  // resurrected from memory with a dead WS. event.persisted === true
  // signals this case.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) probeOrReconnect();
  });

  // Reconnect button — manual retry
  reconnectBtn.addEventListener("click", () => { haptic("tap");
    resetReconnectState();
    setStatus("err", "reconnecting…");
    connect();
  });
}
